// @ts-nocheck
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

interface TrafficData {
  id: number;
  timestamp: string;
  location_id: number;
  device_id: number;
  unique_device_count: number;
  estimated_real_amount: number,
  avg_signal_strength: number;
  min_signal_strength: number;
  max_signal_strength: number;
}

export const useTrafficStore = defineStore('traffic', () => {
  const trafficData = ref<TrafficData[]>([]);
  const locations = ref<any[]>([]);
  const devices = ref<any[]>([]);
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const socket = ref<WebSocket | null>(null);

  // Computed properties
  const totalVisitorsToday = computed(() => {
    return trafficData.value.reduce((sum, item) => sum + item.unique_device_count, 0);
  });

  const trafficByLocation = computed(() => {
    const result: Record<number, number> = {};
    
    trafficData.value.forEach(item => {
      const locationId = item.location_id;
      if (!result[locationId]) {
        result[locationId] = 0;
      }
      result[locationId] += item.unique_device_count;
    });
    
    return result;
  });

  const hourlyTrafficData = computed(() => {
    const hourlyData = new Array(24).fill(0);
    
    trafficData.value.forEach(item => {
      const hour = new Date(item.timestamp).getHours();
      hourlyData[hour] += item.unique_device_count;
    });
    
    return hourlyData;
  });


  function setupWebSocket() {
    if (socket.value?.readyState === WebSocket.OPEN) {
      return; // Already connected
    }
    
    // Close existing connection if any
    if (socket.value) {
      socket.value.close();
    }
    
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}/ws`;
    
    socket.value = new WebSocket(wsUrl);
    
    socket.value.onopen = () => {
      console.log('WebSocket connection established');
    };
    
    socket.value.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        console.log('WebSocket message received:', message.type);
        
        if (message.type === 'welcome') {
          console.log('Connected to WebSocket server - welcome message received');
        } 
        else if (message.type === 'new_traffic_data') {
          // A new traffic data point is available
          console.log('Received new traffic data:', message.data);
          
          // Add the new data to our store
          if (message.data) {
            // Check if we already have this data point
            const exists = trafficData.value.some(item => item.id === message.data.id);
            if (!exists) {
              trafficData.value = [...trafficData.value, message.data];
            }
          }
        }
        else if (Array.isArray(message)) {
          // Handle array of traffic data
          trafficData.value = [...trafficData.value, ...message];
        }
        else if (message.id) {
          // Single traffic data object
          const exists = trafficData.value.some(item => item.id === message.id);
          if (!exists) {
            trafficData.value = [...trafficData.value, message];
          }
        }
      } catch (err) {
        console.error('Error processing WebSocket message:', err, event.data);
      }
    };
    
    socket.value.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
    
    socket.value.onclose = () => {
      console.log('WebSocket connection closed');
      // Try to reconnect after a delay
      setTimeout(setupWebSocket, 5000);
    };
  }

  function closeWebSocket() {
    if (socket.value) {
      socket.value.close();
      socket.value = null;
    }
  }

  async function checkConnection() {
    if (socket.value) {
      return socket.value.readyState == WebSocket.OPEN
    }
    return false
  }

  return {
    trafficData,
    locations,
    devices,
    isLoading,
    error,
    totalVisitorsToday,
    trafficByLocation,
    hourlyTrafficData,
    setupWebSocket,
    closeWebSocket,
    checkConnection
  };
});
